import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../lib/auth';
import { useLang } from '../../lib/lang';
import { getAdminVipPlans, updateAdminVipPlans } from '../../lib/api';
import AdminSidebar from '../../components/AdminSidebar';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { toast } from 'sonner';
import { Save, Plus, Trash2, Crown } from 'lucide-react';

export default function AdminVipPlans() {
  const { user, isAdmin, loading: authLoading } = useAuth();
  const { t } = useLang();
  const navigate = useNavigate();
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (authLoading) return;
    if (!user || !isAdmin) { navigate('/admin/login'); return; }
    loadPlans();
  }, [user, isAdmin, navigate, authLoading]);

  const loadPlans = async () => {
    try {
      const res = await getAdminVipPlans();
      const p = res.data.plans || [];
      if (p.length === 0) {
        // Default plans
        setPlans([
          { id: 'vip_monthly', name: 'VIP Monthly', points_per_month: 200, price_gbp: 150, benefits: ['200 points/month', 'Priority access', 'Exclusive draws', '10% bonus on packs'] },
          { id: 'vip_yearly', name: 'VIP Annual', points_per_month: 250, price_gbp: 1500, benefits: ['250 points/month', 'Priority access', 'Exclusive draws', '15% bonus on packs', 'Birthday bonus'] },
        ]);
      } else {
        setPlans(p);
      }
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const updatePlan = (idx, field, value) => {
    setPlans(prev => prev.map((p, i) => i === idx ? { ...p, [field]: value } : p));
  };

  const updateBenefit = (planIdx, benIdx, value) => {
    setPlans(prev => prev.map((p, i) => {
      if (i !== planIdx) return p;
      const newBenefits = [...(p.benefits || [])];
      newBenefits[benIdx] = value;
      return { ...p, benefits: newBenefits };
    }));
  };

  const addBenefit = (planIdx) => {
    setPlans(prev => prev.map((p, i) => i === planIdx ? { ...p, benefits: [...(p.benefits || []), ''] } : p));
  };

  const removeBenefit = (planIdx, benIdx) => {
    setPlans(prev => prev.map((p, i) => i === planIdx ? { ...p, benefits: (p.benefits || []).filter((_, j) => j !== benIdx) } : p));
  };

  const addPlan = () => {
    const id = `vip_plan_${Date.now()}`;
    setPlans(prev => [...prev, { id, name: 'New Plan', points_per_month: 100, price_gbp: 100, benefits: [] }]);
  };

  const removePlan = (idx) => {
    if (!window.confirm('Delete this plan?')) return;
    setPlans(prev => prev.filter((_, i) => i !== idx));
  };

  const handleSave = async () => {
    try {
      await updateAdminVipPlans({ plans });
      toast.success(t('vipPlansSaved') || 'VIP plans saved');
    } catch { toast.error(t('toastFailed')); }
  };

  if (authLoading || !user || !isAdmin) return null;

  return (
    <div className="flex min-h-screen">
      <AdminSidebar />
      <main className="flex-1 p-6 lg:p-8">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="font-display text-3xl font-bold tracking-tight">{t('vipPlans')}</h1>
            <p className="text-muted-foreground mt-1 text-sm">{t('manageVipDesc')}</p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" className="gap-2 rounded-xl" onClick={addPlan}><Plus className="w-4 h-4" /> {t('addPlan')}</Button>
            <Button className="bg-primary text-primary-foreground btn-press gap-2 rounded-xl" onClick={handleSave}><Save className="w-4 h-4" /> {t('saveAll')}</Button>
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-20"><div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" /></div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {plans.map((plan, idx) => (
              <Card key={plan.id || idx} className="bg-white border-border">
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-lg flex items-center gap-2"><Crown className="w-5 h-5 text-primary" /> Plan {idx + 1}</CardTitle>
                    <Button size="icon" variant="ghost" className="text-red-500" onClick={() => removePlan(idx)}><Trash2 className="w-4 h-4" /></Button>
                  </div>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>{t('planId')}</Label>
                      <Input value={plan.id} onChange={(e) => updatePlan(idx, 'id', e.target.value)} className="rounded-xl font-mono text-sm" />
                    </div>
                    <div className="space-y-2">
                      <Label>{t('planName')}</Label>
                      <Input value={plan.name} onChange={(e) => updatePlan(idx, 'name', e.target.value)} className="rounded-xl" />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>{t('pointsPerMonth')}</Label>
                      <Input type="number" value={plan.points_per_month} onChange={(e) => updatePlan(idx, 'points_per_month', parseInt(e.target.value) || 0)} className="rounded-xl" />
                    </div>
                    <div className="space-y-2">
                      <Label>{t('priceGbp')}</Label>
                      <Input type="number" value={plan.price_gbp} onChange={(e) => updatePlan(idx, 'price_gbp', parseFloat(e.target.value) || 0)} className="rounded-xl" />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <Label>{t('benefitsList')}</Label>
                      <Button size="sm" variant="ghost" className="gap-1 text-xs" onClick={() => addBenefit(idx)}><Plus className="w-3 h-3" /> {t('addBenefit')}</Button>
                    </div>
                    <div className="space-y-2">
                      {(plan.benefits || []).map((b, bIdx) => (
                        <div key={bIdx} className="flex gap-2">
                          <Input value={b} onChange={(e) => updateBenefit(idx, bIdx, e.target.value)} className="rounded-xl text-sm flex-1" placeholder="e.g. 200 points/month" />
                          <Button size="icon" variant="ghost" className="text-red-400 shrink-0" onClick={() => removeBenefit(idx, bIdx)}><Trash2 className="w-3 h-3" /></Button>
                        </div>
                      ))}
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
